import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Star, Search, Crown, ListChecks, MessageCircle, Send, Phone, Package } from "lucide-react";
import { toast } from "sonner";
import { api, whatsappLink, telegramLink, waNumber } from "../lib/api";
import { useI18n } from "../i18n";
import { usePageTitle } from "../lib/usePageTitle";

const STATUS = {
  nouvelle: { label: "lo.stNew", color: "text-zinc-300 border-zinc-600" },
  acceptee: { label: "lo.stAcc", color: "text-neon-green border-neon-green" },
  en_livraison: { label: "lo.stDel", color: "text-neon-magenta border-neon-magenta" },
  livree: { label: "lo.stDone", color: "text-neon-green border-neon-green" },
  refusee: { label: "lo.stRef", color: "text-red-400 border-red-500" },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function Loyalty() {
  const { t } = useI18n();
  usePageTitle(
    "Fidélité & suivi de commande — STUFF CBD",
    "Consulte tes points fidélité STUFF CBD et suis tes commandes en direct avec ton numéro de téléphone."
  );
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const lookup = async (e) => {
    e && e.preventDefault();
    if (!phone) { toast.error(t("lo.enterPhone")); return; }
    setLoading(true);
    try {
      const [l, g, o] = await Promise.all([
        api.get(`/loyalty/${encodeURIComponent(phone.trim())}`),
        api.get("/gifts"),
        api.get(`/orders/track/${encodeURIComponent(phone.trim())}`),
      ]);
      setData(l.data);
      setGifts(g.data);
      setOrders(o.data);
      setSearched(true);
    } catch { toast.error(t("lo.error")); }
    finally { setLoading(false); }
  };

  const redeem = async (gift) => {
    if ((data?.points || 0) < gift.points_cost) { toast.error(t("lo.noPts")); return; }
    if (!window.confirm(`${t("lo.confirm1")} ${gift.points_cost} ${t("lo.confirm2")} "${gift.name}" ?`)) return;
    try {
      const { data: red } = await api.post("/redemptions", { phone: phone.trim(), gift_id: gift.id });
      setData((d) => ({ ...d, points: red.remaining_points }));
      toast.success(`${t("lo.redeemed1")} ${red.ref}. ${t("lo.redeemed2")}`, { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
    } catch (err) { toast.error(err.response?.data?.detail || t("lo.error")); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="text-center mb-8">
        <Crown className="h-8 w-8 text-neon-green mx-auto mb-2" />
        <h1 className="font-anton text-4xl sm:text-5xl uppercase text-white">{t("lo.title1")} <span className="neon-text-green">{t("lo.title2")}</span></h1>
        <p className="text-zinc-400 mt-2">{t("lo.sub")}</p>
      </div>

      <form onSubmit={lookup} className="flex gap-2 max-w-md mx-auto mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
          <input data-testid="loyalty-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("lo.phone")} className="w-full bg-ink-surface border border-zinc-700 text-white pl-10 pr-4 py-3 focus:outline-none focus:border-neon-green" />
        </div>
        <button type="submit" data-testid="loyalty-lookup" disabled={loading} className="bg-neon-green text-black font-anton uppercase px-5 glow-green disabled:opacity-50">{t("lo.see")}</button>
      </form>

      {data && (
        <div className="bg-ink-surface border border-neon-green/40 p-6 text-center mb-8" data-testid="loyalty-balance">
          <p className="text-zinc-400 uppercase text-sm">{data.name ? `${t("lo.hi")} ${data.name},` : t("lo.yourPts")} {t("lo.youHave")}</p>
          <div className="font-anton text-6xl neon-text-green my-2">{data.points}</div>
          <p className="text-zinc-400 uppercase text-sm">{t("lo.points")}</p>
        </div>
      )}

      {/* Mes commandes */}
      {searched && (
        <div className="mb-10">
          <h2 className="font-anton text-2xl uppercase text-white mb-4 flex items-center gap-2"><ListChecks className="h-6 w-6 text-neon-green" /> {t("lo.myOrders")}</h2>
          {orders.length === 0 ? (
            <p className="text-zinc-500 flex items-center gap-2"><Package className="h-4 w-4" /> {t("lo.noOrders")}</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => {
                const st = STATUS[o.status] || { label: o.status, color: "text-zinc-300 border-zinc-600" };
                const wa = whatsappLink(o.vendor_whatsapp || o.vendor_phone, `${t("tr.msg")} #${o.ref}`);
                const tg = telegramLink(o.vendor_telegram, `${t("tr.msg")} #${o.ref}`);
                const tel = waNumber(o.vendor_phone || o.vendor_whatsapp);
                return (
                  <div key={o.ref} data-testid="track-order-row" className="bg-ink-surface border border-zinc-800 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="font-mono text-neon-green">#{o.ref}</span>
                        <span className="text-zinc-500 text-xs ml-2">{fmtDate(o.created_at)}</span>
                        <span className="text-zinc-500 text-xs ml-2">· {o.vendor_name}</span>
                      </div>
                      <span data-testid="track-order-status" className={`text-xs uppercase font-bold px-2 py-1 border ${st.color}`}>{t(st.label)}</span>
                    </div>
                    <div className="text-zinc-400 text-sm mt-2">
                      {o.items.map((i, idx) => <span key={`${i.product_id || i.name}-${idx}`} className="mr-3">{i.qty}x {i.name}</span>)}
                      <span className="text-white font-anton ml-1">— {Number(o.total).toFixed(2)}€</span>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {wa && <a data-testid="track-whatsapp" href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 bg-neon-green text-black"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
                      {tg && <a data-testid="track-telegram" href={tg} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 bg-neon-magenta text-white"><Send className="h-3.5 w-3.5" /> Telegram</a>}
                      {tel && <a data-testid="track-call" href={`tel:${tel}`} className="inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-neon-green"><Phone className="h-3.5 w-3.5" /> {t("tr.call")}</a>}
                      <Link data-testid="track-detail" to={`/suivi/${o.ref}`} className="inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-neon-green/60 text-neon-green hover:bg-neon-green hover:text-black transition-colors"><ListChecks className="h-3.5 w-3.5" /> {t("lo.follow")}</Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {data && (
        <>
          <h2 className="font-anton text-2xl uppercase text-white mb-4 flex items-center gap-2"><Gift className="h-6 w-6 text-neon-magenta" /> {t("lo.gifts")}</h2>
          {gifts.length === 0 ? <p className="text-zinc-500">{t("lo.noGifts")}</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {gifts.map((g) => {
                const can = data.points >= g.points_cost;
                return (
                  <div key={g.id} data-testid="gift-card" className="bg-ink-surface border border-zinc-800 p-5 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-anton text-lg uppercase text-white">{g.name}</h3>
                      <span className="flex items-center gap-1 text-neon-green font-bold text-sm"><Star className="h-4 w-4 fill-neon-green" /> {g.points_cost}</span>
                    </div>
                    {g.description ? <p className="text-zinc-500 text-sm mb-4 flex-1">{g.description}</p> : <div className="flex-1" />}
                    <button data-testid="redeem-gift" onClick={() => redeem(g)} disabled={!can} className={`uppercase font-bold text-sm py-2 transition-colors ${can ? "bg-neon-green text-black glow-green" : "bg-zinc-800 text-zinc-500 cursor-not-allowed"}`}>
                      {can ? t("lo.redeem") : `${t("lo.more1")} ${g.points_cost - data.points} ${t("lo.more2")}`}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
